import { AccountType } from 'config'
import type { IBorrowerTypeLimit, ICrossCollateralBlanket, IExtensionCost } from 'pages/LoanStructure/interfaces'
import { LoanType } from 'reducers/loanDetail.reducer'

export interface ILtvValues {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
}

export interface ILoanPurposeLTVValues {
  purchase: ILtvValues
  nocashout: ILtvValues
  cashout: ILtvValues
}

export interface IRange {
  from: number
  to: number
}

export interface ILoanPurposeValues {
  purchase: number
  nocashout: number
  cashout: number
}

export interface ILabelValue {
  label: string
  value: string
}

export interface IMaxCashToBorrowerLeverageLimit {
  ltv: IRange
  citizenship: string[]
  limit: {
    FICO: IRange
    loanAmount: IMinMaxLimit
    minDSCR: number
    maxValue: number
    maxLoanAmount: number
  }[]
}

export interface INumberOfPropertiesLimit {
  min: number
  max: number
}

export interface IMinMaxLimit {
  min: number
  max: number
}

export interface INonDscrLTVs {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
  cltv: number
}

export interface INonDSCRLoanPurposeLTVValues {
  purchase: INonDscrLTVs
  nocashout: INonDscrLTVs
  cashout: INonDscrLTVs
}

export interface ISecondLienLimit {
  maxCLTV: number
  maxSecondLien: number
}

// Non-DSCR leverage limit interfaces
export interface IFICORangeLimits {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: ILoanPurposeLTVValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IExperienceLtvMaxLimits {
  from: number
  to: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: ILoanPurposeLTVValues
    minMonthsReserve: ILoanPurposeValues
    maxConstructionBudget: ILoanPurposeValues
  }[]
}

export interface IFICORangeLTVLimit {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: INonDSCRLoanPurposeLTVValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IPropertyTypeLeverageData {
  value: string
  numberOfUnits: IRange
  maxVacant: number
  maxVacantIsPercentage: boolean
  citizenship: string[]
  states: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  minExperience: number
  limits: IFICORangeLTVLimit[]
}

export interface IStateLeverageLimit {
  value: string
  citizenship: string[]
  borrowerTypes: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLTVLimit[]
}

export interface ICitizenshipLtvMaxLimits {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: {
    FICO: IRange
    LTV: ILoanPurposeLTVValues
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    minExperience: number
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IBankruptcyRange {
  chapter: number
  from: number
  to: number
}

export interface IMortgageLatesRange {
  from: number
  to: number
  days: number
  months: number
}

export interface IValueLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IFirstTimeInvestorLeverageLimit {
  value: string
  propertyTypes: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IBankruptcyLeverageLimit {
  value: IBankruptcyRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IFICORangeLimits[]
}

export interface IMortgageLatesLeverageLimit {
  value: IMortgageLatesRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLimits[]
}

export interface IRangeLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IFICORangeLimits[]
}

export interface IRuralPropertyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: {
    FICO: IRange
    propertyTypes: string[]
    LTV: ILoanPurposeLTVValues
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IRehabBudgetTypeLimit {
  expRange: IRange
  loanAmount: IMinMaxLimit
  limits: IFICORangeLimits[]
}

export interface IRehabBudgetTypeLtvMaxLimits {
  value: string
  limits: IRehabBudgetTypeLimit[]
}

export interface ILeveragesLimit {
  value: IRange
  loanAmount: IMinMaxLimit
  minFICO: number
  limits: IFICORangeLTVLimit[]
}

export interface ILeveragesLimits {
  value: string
  limits: ILeveragesLimit[]
}

export interface IFicoLeverageLimit {
  ficoRange: IRange
  citizenship: string[]
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: ILoanPurposeLTVValues
  minMonthsReserve: ILoanPurposeValues
  minExperience: ILoanPurposeValues
}

export interface ILoanAmountLeverageLimit {
  loanAmount: IRange
  LTV: ILoanPurposeLTVValues
}

export interface IOtherLimit {
  minAsIsAppraisedValue: ILoanPurposeValues
  minAfterRepairValue: ILoanPurposeValues
  minAIVPricePerDoor: ILoanPurposeValues
  minARVPricePerDoor: ILoanPurposeValues
}

export interface IBPieceLeverageLimit {
  aPieceLeverageLimit: INonDSCRLoanPurposeLTVValues
  totalLeverageLimit: INonDSCRLoanPurposeLTVValues
}

export interface ILeverageMaxLimits {
  experienceLtvLimits: IExperienceLtvMaxLimits[]
  propertyTypeLtvLimits: IPropertyTypeLeverageData[]
  occupancyLeverageLimit: IValueLeverageLimit[]
  stateLeverageLimit: IStateLeverageLimit[]
  decliningMarketPropertyLeverageLimit: IValueLeverageLimit[]
  newConstructionPropertyLeverageLimit: IValueLeverageLimit[]
  firstTimeHomeBuyerLeverageLimit: IValueLeverageLimit[]
  firstTimeInvestorLeverageMaxLimit: IFirstTimeInvestorLeverageLimit[]
  bankruptcyLeverageMaxLimit: IBankruptcyLeverageLimit[]
  mortgageLatesLtvLimits: IMortgageLatesLeverageLimit[]
  fcSsDilLeverageMaxLimit: IRangeLeverageLimit[]
  forbearanceLeverageLimit: IRangeLeverageLimit[]
  citizenshipLtvLimits: ICitizenshipLtvMaxLimits[]
  ruralPropertyLeverageMaxLimit: IRuralPropertyLeverageLimit[]
  rehabBudgetTypeLtvLimits: IRehabBudgetTypeLtvMaxLimits[]
  leveragesLimits: ILeveragesLimits[]
  ficoLeverageLimit: IFicoLeverageLimit[]
  loanAmountLeverageLimit: ILoanAmountLeverageLimit[]
  maxCashToBorrowerLeverageMaxLimit: IMaxCashToBorrowerLeverageLimit[]
  crossCollateralBlanket: ICrossCollateralBlanket
  secondLienMaxLimit: ISecondLienLimit
  bPieceLeverageLimit: IBPieceLeverageLimit
  tierLeverageLimit: ITierLeverageLimit[]
  otherLimit: IOtherLimit
}

// DSCR leverage limit interfeaces
export interface IDscrLTVs {
  ltv: IMinMaxLimit
  cltv: number
}

export interface IDscrFicoLeverageLimit {
  ficoRange: IRange
  citizenship: string[]
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  minDSCR: ILoanPurposeValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IDSCRFICORangeLimits {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  minDSCR: ILoanPurposeValues
  minMonthsReserve: ILoanPurposeValues
}

export interface IFICORangeLTVLimitsForDSCR {
  FICO: IRange
  loanAmount: {
    purchase: IMinMaxLimit
    nocashout: IMinMaxLimit
    cashout: IMinMaxLimit
  }
  LTV: {
    purchase: IDscrLTVs
    nocashout: IDscrLTVs
    cashout: IDscrLTVs
  }
  DSCR: { purchase: IRange; nocashout: IRange; cashout: IRange }
  minMonthsReserve: ILoanPurposeValues
  minDSCR: ILoanPurposeValues
}

export interface IDscrPropertyTypeLeverageLimit {
  value: string
  numberOfUnits: IRange
  maxVacant: number
  maxVacantIsPercentage: boolean
  citizenship: string[]
  states: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IFICORangeLTVLimitsForDSCR[]
}

export interface IDscrStateLeverageLimit {
  value: string
  citizenship: string[]
  borrowerTypes: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IFICORangeLTVLimitsForDSCR[]
}

export interface IDscrEscrowTypeLeverageLimit {
  value: string
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrValueLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrOccupancyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: {
      purchase: IDscrLTVs
      nocashout: IDscrLTVs
      cashout: IDscrLTVs
    }
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
    minPPP: ILoanPurposeValues
  }[]
}

export interface IDscrFirstTimeInvestorLeverageLimit {
  value: string
  propertyTypes: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrBankruptcyLeverageLimit {
  value: IBankruptcyRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrMortgageLatesLeverageLimit {
  value: IMortgageLatesRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrCustomRangeLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrShortTermRentalLeverageLimit {
  value: string
  citizenship: string[]
  firstTimeInvestor: string[]
  firstTimeHomeBuyer: string[]
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: IDSCRFICORangeLimits[]
}

export interface IDscrRuralPropertyLeverageLimit {
  value: string
  loanAmount: IMinMaxLimit
  LTV: number
  CLTV: number
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    LTV: {
      purchase: IDscrLTVs
      nocashout: IDscrLTVs
      cashout: IDscrLTVs
    }
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
    propertyTypes: string[]
  }[]
}

export interface IInterestOnlyLimit {
  citizenship: string[]
  limit: {
    fico: IRange
    minDSCR: number
    loanAmount: IRange
    DSCR: IRange
    minMonthsReserve: number
    LTV: IMinMaxLimit
    maxCLTV: number
  }[]
}

export interface IDscrInterestOnlyLeverageLimit {
  purchase: IInterestOnlyLimit[]
  nocashout: IInterestOnlyLimit[]
  cashout: IInterestOnlyLimit[]
}

export interface IDscrRangeLeverageLimit {
  dscr: IRange
  propertyTypes: string[]
  states: string[]
  minFICO: number
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    limit: {
      purchase: IDscrLTVs
      nocashout: IDscrLTVs
      cashout: IDscrLTVs
    }
  }[]
}

export interface IDscrLoanAmountLeverageLimit {
  loanAmount: IRange
  LTV: ILoanPurposeValues
  CLTV: ILoanPurposeValues
  minDSCR: ILoanPurposeValues
}

export interface IDscrLTVLeverageLimit {
  value: IRange
  citizenship: string[]
  loanAmount: IMinMaxLimit
  minFICO: number
  maxMortgageLates: IMortgageLatesRange
  limits: {
    FICO: IRange
    loanAmount: {
      purchase: IMinMaxLimit
      nocashout: IMinMaxLimit
      cashout: IMinMaxLimit
    }
    CLTV: ILoanPurposeValues
    minDSCR: ILoanPurposeValues
    minMonthsReserve: ILoanPurposeValues
  }[]
}

export interface IDscrExperienceLeverageLimit {
  value: IRange
  limits: IDSCRFICORangeLimits[]
}

export interface ITierLeverageLimit {
  value: number
  propertyTypes: string[]
  citizenship: string[]
  borrowerTypes: string[]
  maxBorrowerExposure: number
  minLoanAmount: number
  maxLoanAmount: number
  LTV: ILtvValues
  otherEligible: { ruralProperty: boolean }
}

export interface IDscrLeverageMaxLimits {
  PropertyTypeLtvMaxLimit: IDscrPropertyTypeLeverageLimit[]
  OccupancyLeverageLimit: IDscrOccupancyLeverageLimit[]
  EscrowTypeLeverageLimit: IDscrEscrowTypeLeverageLimit[]
  StateLeverageLimit: IDscrStateLeverageLimit[]
  ExperienceLeverageLimit: IDscrExperienceLeverageLimit[]
  CitizenshipLtvMaxLimit: IDscrValueLeverageLimit[]
  DecliningMarketPropertyLeverageLimit: IDscrValueLeverageLimit[]
  NewConstructionPropertyLeverageLimit: IDscrValueLeverageLimit[]
  FirstTimeHomeBuyerLeverageLimit: IDscrValueLeverageLimit[]
  FirstTimeInvestorLeverageMaxLimit: IDscrFirstTimeInvestorLeverageLimit[]
  BankruptcyLeverageMaxLimit: IDscrBankruptcyLeverageLimit[]
  MortgageLatesLtvMaxLimit: IDscrMortgageLatesLeverageLimit[]
  FcSsDilLeverageMaxLimit: IDscrCustomRangeLeverageLimit[]
  ForbearanceLeverageLimit: IDscrCustomRangeLeverageLimit[]
  IsShortTermRentalLeverageLimit: IDscrShortTermRentalLeverageLimit[]
  RuralPropertyLeverageMaxLimit: IDscrRuralPropertyLeverageLimit[]
  InterestOnlyLeverageLimit: IDscrInterestOnlyLeverageLimit
  FICOLeverageLimit: IDscrFicoLeverageLimit[]
  DscrLeverageLimit: IDscrRangeLeverageLimit[]
  LoanAmountLeverageLimit: IDscrLoanAmountLeverageLimit[]
  LTVLeverageLimit: IDscrLTVLeverageLimit[]
  MaxCashToBorrowerLeverageMaxLimit: IMaxCashToBorrowerLeverageLimit[]
  CrossCollateralBlanket: ICrossCollateralBlanket
  SecondLienMaxLimit: ISecondLienLimit
  TierLeverageLimit: ITierLeverageLimit[]
}

export interface IPropertyTypeLeverageAdjustment {
  value: string
  numberOfUnits: IRange
  maxVacant: number
  maxVacantIsPercentage: boolean
  limit: ILoanPurposeLTVValues
}

export interface IValueLeverageData {
  value: string
  limit: ILoanPurposeLTVValues
}

export interface IAdvancedLeverageAdjustment {
  value: string
  adjustment: {
    loanAmount: IRange
    LTV: ILoanPurposeLTVValues
  }[]
}

export interface IBankruptcyLeverageAdjustment {
  value: IBankruptcyRange
  limit: ILoanPurposeLTVValues
}

export interface IMortgageLatesLeverageAdjustment {
  value: IMortgageLatesRange
  limit: ILoanPurposeLTVValues
}

export interface IRangeLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeLTVValues
}

export interface IFicoLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeLTVValues
  propertyTypes: string[]
}

export interface ILeverageAdjustments {
  maxLTVAdjustment: ILtvValues
  propertyTypeLeverageAdjustment: IPropertyTypeLeverageAdjustment[]
  occupancyLeverageAdjustment: IValueLeverageData[]
  underConstructionLeverageAdjustment: IValueLeverageData[]
  isPropertyInLeasableStateLeverageAdjustment: IValueLeverageData[]
  newConstructionPropertyLeverageAdjustment: IValueLeverageData[]
  citizenshipLeverageAdjustment: IValueLeverageData[]
  rehabBudgetTypeLeverageAdjustment: IValueLeverageData[]
  ruralPropertyLeverageAdjustment: IValueLeverageData[]
  firstTimeHomeBuyerLeverageAdjustment: IValueLeverageData[]
  firstTimeInvestorLeverageAdjustment: IValueLeverageData[]
  interestOnlyLeverageAdjustment: IValueLeverageData[]
  decliningMarketPropertyLeverageAdjustment: IAdvancedLeverageAdjustment[]
  ficoLeverageAdjustment: IFicoLeverageAdjustment[]
  bankruptcyLeverageAdjustment: IBankruptcyLeverageAdjustment[]
  mortgageLatesLeverageAdjustment: IMortgageLatesLeverageAdjustment[]
  fcSsDilLeverageAdjustment: IRangeLeverageAdjustment[]
  forbearanceLeverageAdjustment: IRangeLeverageAdjustment[]
  exitFeeLeverageAdjustment: IRangeLeverageAdjustment[]
  loanAmountLeverageAdjustment: IRangeLeverageAdjustment[]
  monthsReserveLeverageAdjustment: IRangeLeverageAdjustment[]
  experienceLeverageAdjustment: IRangeLeverageAdjustment[]
  loanPurposeLeverageAdjustment: ILoanPurposeLTVValues
  tierLeverageAdjustment: ITierLeverageLimit[]
}

export interface IDscrPropertyTypeLeverageData {
  value: string
  numberOfUnits: IRange
  maxVacant: number
  maxVacantIsPercentage: boolean
  limit: ILoanPurposeValues
}

export interface IDscrValueLeverageData {
  value: string
  limit: ILoanPurposeValues
}

export interface IDSCRAdvancedLeverageAdjustment {
  value: string
  adjustment: {
    loanAmount: IRange
    LTV: ILoanPurposeValues
  }[]
}

export interface IDscrBankruptcyLeverageAdjustment {
  value: IBankruptcyRange
  limit: ILoanPurposeValues
}

export interface IDscrMortgageLatesLeverageAdjustment {
  value: IMortgageLatesRange
  limit: ILoanPurposeValues
}

export interface IDscrRangeLeverageAdjustment {
  range: IRange
  limit: ILoanPurposeValues
}

export interface IDscrLeverageAdjustments {
  MaxLTVAdjustment: { LTV: number; CLTV: number }
  PropertyTypeLeverageAdjustment: IDscrPropertyTypeLeverageData[]
  LoanAmountLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  FcSsDilLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  ForbearanceLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  DSCRLeverageAdjustment: IDscrRangeLeverageAdjustment[]
  BankruptcyLeverageAdjustment: IDscrBankruptcyLeverageAdjustment[]
  MortgageLatesLeverageAdjustment: IDscrMortgageLatesLeverageAdjustment[]
  OccupancyLeverageAdjustment: IDscrValueLeverageData[]
  CitizenshipLeverageAdjustment: IDscrValueLeverageData[]
  IsPropertyInLeasableStateLeverageAdjustment: IDscrValueLeverageData[]
  IsShortTermRentalLeverageAdjustment: IDscrValueLeverageData[]
  NewConstructionPropertyLeverageAdjustment: IDscrValueLeverageData[]
  FirstTimeHomeBuyerLeverageAdjustment: IDscrValueLeverageData[]
  FirstTimeHomeInvestorLeverageAdjustment: IDscrValueLeverageData[]
  InterestOnlyLeverageAdjustment: IDscrValueLeverageData[]
  DecliningMarketPropertyLeverageAdjustment: IDSCRAdvancedLeverageAdjustment[]
  TierLeverageAdjustment: ITierLeverageLimit[]
}

export interface ILoanAmountLimits {
  loanAmount: IRange
  initialLoanAmount: IRange
}

export interface IRatePriceAdjustment {
  key: string
  keys?: string[]
  values: IRatePriceAdjustmentValue[]
}

export interface IRatePriceAdjustmentValue {
  value: any
  price: ILoanPurposeValues
  rate: ILoanPurposeValues
}

export interface IVisibleOptionValue {
  visible: boolean
  userTypes: AccountType[]
  channelTypes: LoanType[]
}

export interface IVisibleOptions {
  price: IVisibleOptionValue
  exitFee: IVisibleOptionValue
  rateSpread: IVisibleOptionValue
}

export interface ICommercialOnlyProgrammed {
  leverageMaxLimits?: ILeverageMaxLimits
  leverageAdjustments?: ILeverageAdjustments
  loanAmountLimits: ILoanAmountLimits
  baseRates: number[]
  basePrices: number[]
  rateSpreads: number[]
  exitFees: number[]
  ltvLimits: INonDSCRLoanPurposeLTVValues
  ratePriceAdjustments: IRatePriceAdjustment[]
  visibleOptions: IVisibleOptions
}

export interface IVisibleMenus {
  key: string
  visible: boolean
}

export interface IProgramTableData {
  ID: number
  Name: string
  Type: string
  tableData: any[]
  tableStyle: Record<string, any>
  leverageMaxLimits?: ILeverageMaxLimits
  leverageAdjustments?: ILeverageAdjustments
  StaticTable: IStaticTable[]
  LockExtensionCost: IExtensionCost
  BorrowerTypeLimit: IBorrowerTypeLimit
  LoanPurposeDefinition: ILoanPurposeDefinition
  programData: ICommercialOnlyProgrammed
}

export interface ILoanAmountLimits {
  loanAmount: IRange
  initialLoanAmount: IRange
}

export interface ILtvValues {
  aivLtv: number
  arvLtv: number
  ltc: number
  ltp: number
}

export interface IPropertyTypeLtvAdj {
  propertyType: string
  unitsRange: IRange
  aivValue: IRange
  arvValue: IRange
  ltvLimit: ILoanPurposeLTVValues
  ltvAdjustment: ILoanPurposeLTVValues
}

export interface IExperienceTierLimit {
  expRange: IRange
  limit: {
    FICO: IRange
    maxLoanAmount: ILoanPurposeValues
    ltvLimit: ILoanPurposeLTVValues
    ltvAdjustment: ILoanPurposeLTVValues
  }[]
}

export interface IExperienceTierData {
  title: string
  count: number
  limit: IExperienceTierLimit[]
}

export interface ITier {
  title: string
  count: number
  propertyTypes?: string[]
  ltvLimit?: ILoanPurposeLTVValues
  ltvAdjustment?: ILoanPurposeLTVValues
  propertyTypeLtvAdj?: IPropertyTypeLtvAdj[]
}

export interface IExperienceTier {
  [key: string]: IExperienceTierData[]
}

export interface ITierData {
  [key: string]: ITier[] | IExperienceTier
}

export interface IStaticTable {
  title: string
  values: string[]
}

export interface IBridgeRate {
  FICO: IRange
  interestRates: number[]
}

export interface IBridgeRatePriceAdjustment {
  FICO: IRange
  LLPA: number[]
  LLRA: number[]
}

export interface IBridgeRatePrice {
  rate: IBridgeRate[]
  ratePriceAdjustment?: IBridgeRatePriceAdjustment[]
  price: number
  exitFee: number
  rateSpread: number
  incremental: {
    rate: number
    price: number
    exitFee: number
    rateSpread: number
  }
  maximum: {
    rate: number
    price: number
    exitFee: number
    rateSpread: number
  }
}

export enum NonDSCRTemplateType {
  NonLeverageBased,
  LeverageBased,
}

export interface IRatePriceLimitWithExitFee {
  exitFee: IRange
  rate: IMinMaxLimit
  price: IMinMaxLimit
}

export interface IRatePriceLimitWithRange {
  value: IRange
  rate: IMinMaxLimit
  price: IMinMaxLimit
}

export interface IPrepaymentPenaltyRatePriceLimit {
  value: number
  subValue: string
  interestOption: number
  monthsInterest: number
  rate: IMinMaxLimit
  price: IMinMaxLimit
}

export interface IPropertyTypeRatePriceLimit {
  propertyType: string
  numberOfUnits: IRange
  rate: IMinMaxLimit
  price: IMinMaxLimit
}

export interface IRatePriceLimit {
  exitFeeLimit?: IRatePriceLimitWithExitFee[]
  rateLimit?: IMinMaxLimit
  loanAmountLimit?: IRatePriceLimitWithRange[]
  prepaymentPenaltyLimit?: IPrepaymentPenaltyRatePriceLimit[]
  propertyTypeRatePriceLimit?: IPropertyTypeRatePriceLimit[]
  stateRatePriceLimit?: IStateRatePriceLimit[]
}

export interface IStateRatePriceLimit {
  state: string
  rate: IMinMaxLimit
  price: IMinMaxLimit
}

export interface ITransactionTypeValue {
  Purchase: number
  Refinance: number
}

export interface IPPPLoanAmountLimit {
  ppp: number
  loanAmount: IMinMaxLimit
}

export interface IPropertyTypeValue {
  propertyType: string
  numberOfUnits: IRange
}

export interface IStateRestriction {
  state: string
  propertyTypes: IPropertyTypeValue[]
  borrowerTypes: string[]
  amortizationTypes: string[]
  pppRequired: {
    Purchase: string
    Refinance: string
  }
  pppPermitted: {
    Purchase: string
    Refinance: string
  }
  loanAmountRange: {
    Purchase: IRange
    Refinance: IRange
  }
  pppLimit: {
    min: ITransactionTypeValue
    max: ITransactionTypeValue
  }
  pppLoanAmountLimit: IPPPLoanAmountLimit[]
}

export interface ITierMatrix {
  experienceTierMatrix: IRangeTierMatrix[]
}

export interface IRangeTierMatrix {
  value: IRange
  matrix: {
    FICO: IRange
    tier: number
  }[]
}

export interface IProgramData {
  ID: number
  Name: string
  Type: string
  ProductType: string
  TemplateType: NonDSCRTemplateType
  Restriction: IDscrRestriction
  ProgramData: ICommercialOnlyProgrammed
  VisibleMenus: IVisibleMenus[]
  States: string[]
  RatePriceLimit: IRatePriceLimit
  StatesRestriction: IStateRestriction[]
  LTV: IRange[]
  BridgeData: IBridgeRatePrice
  TierData: ITierData
  StaticTable: IStaticTable[]
  LockExtensionCost: IExtensionCost
  BorrowerTypeLimit: IBorrowerTypeLimit
  LoanPurposeDefinition: ILoanPurposeDefinition
  TierMatrix: ITierMatrix
  InvestorPriceLimit: IInvestorPriceLimit
}

export interface IInvestorPriceLimit {
  prepaymentPenaltyLimit?: IPrepaymentPenaltyRatePriceLimit[]
  loanAmountLimit?: IRatePriceLimitWithRange[]
}

export interface ILoanProgram {
  id: number
  no: string
  programCode: string
  productName: string
  programID: number
  mortgageType: string
  loanTerm: string
  amortizationType: string
  interestOnly: boolean
  ballonPayment: boolean
  negativeAmortization: boolean
  otherNonAmortizingFeature: boolean
  ioMonths: number
  jumbo: string
  ARMIndexValue: number
  ARMIntRateFixedFor: number
  ARMIntRateAdjustAt: number
  ARMIndexValuesLocked: boolean
  ARMLifeCap: number
  ARMFloor: number
  ARMNoAdjCap: boolean
  ARMAdjustCapFirst: number
  ARMAdjustCapSubsequent: number
  ARMRounding: number
  ARMMaxRate: number
  ARMCommonRenewal: number
  ARMMargin: number
  ARMHasNegAmFeature: boolean
  ARMIndexType: number
  ARMIndexNameOV: string
  ARMIndexPublished: string
  ARMIndexValueForWeekEnding: string
  ARMLookbackDays: number
  ARMInterestRateChangesDescOV: string
  ARMPaymentChangesDescOV: string
  ARMNegAmDesc: string
  ARMAddFeatures: string
  DemandFeature: boolean
  ARMCarryover: boolean
  ARMConversationOption: boolean
  createdAt: Date
  updatedAt: Date
}

export interface ILoanPurposeDefinition {
  nocashout: {
    floor?: number
    percentage: number
  }
  cashout: {
    floor?: number
    percentage: number
  }
}

export interface ILoanAmountDscrOverlay {
  loanAmount: IRange
  minDscr: number
}

export interface ILoanPurposeDscrOverlay {
  loanPurpose: string
  minDscr: number
}

export interface IDscrOverlay {
  loanAmountDscrOverlay?: ILoanAmountDscrOverlay[]
  loanPurposeDscrOverlay?: ILoanPurposeDscrOverlay[]
}

export interface ILTVData {
  title: string
  DSCR?: IRange
  data: ILoanAmountFICOMaxLTV[]
}

export interface IMatrixLTVData {
  label: string
  key: string
  occupancy: string
  maxLTVs: ILTVData[]
}

export interface IDscrRestriction {
  MatrixLTVData?: IMatrixLTVData[]
  PPPOverlay?: IPrepaymentPenaltyOverlay[]
  LeverageMaxLimits?: IDscrLeverageMaxLimits
  LeverageAdjustments?: IDscrLeverageAdjustments
  DscrOverlay?: IDscrOverlay
  DscrOccupancyLtvReduction?: number
  MinRate?: number
  RateReduction?: number
}

export interface ILoanAmountFICOMaxLTV {
  loanAmount: IRange
  ficos: number[]
  monthsReserve: number
  purchaseRT: number[]
  nocashout: number[]
  cashout: number[]
}

export interface Ltv {
  title: string
  data: ILoanAmountFICOMaxLTV[]
}

export interface StateLTVMaxLimit {
  label: string
  limits: Record<string, number>
}

export enum DocumentType {
  GUIDELINE = 'guideline',
  FORM = 'form',
  TRAINING = 'training',
  RESISDENTIAL = 'resisdential',
  MARKETING = 'marketing',
}

export interface IDocument {
  id: number
  name: string
  orderId: number
  fileKey: string
  links?: string[]
  type: DocumentType
  programIDs: string[]
  createdAt: Date
  updatedAt: Date
}

export interface IPrepaymentPenaltyOverlay {
  label: string
  // minPPP: number
  limit: ILoanPurposeValues
}

export interface IPPPOverlayConfig {
  listedMonths: IRange
  overlays: IPrepaymentPenaltyOverlay[]
}
